import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { Form } from "../../../../ui";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  category: yup.string().required("Category is required"),
  unit: yup.string().required("Unit is required"),
});

const defaultValues = {
  name: "",
  sku: "",
  category: "bed",     // bed | bath | kitchen | cleaning | paper_goods | maintenance | other
  unit: "each",        // each | pack | box | case
  reorder_point: "",
  notes: "",
  active: true,
};

const ITEM_CATEGORY_OPTIONS = [
  { label: "Bed", value: "bed" },
  { label: "Bath", value: "bath" },
  { label: "Kitchen", value: "kitchen" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Paper Goods", value: "paper_goods" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Other", value: "other" },
];

const UNIT_OPTIONS = [
  { label: "Each", value: "each" },
  { label: "Pack", value: "pack" },
  { label: "Box", value: "box" },
  { label: "Case", value: "case" },
];

const StorageItemAdd = ({
  item,            // existing item or null
  flagIsNew,       // boolean
  flagIsProcessing,
  onSave,          // async (payload) => true/false
  onCancel,        // () => void
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const active = watch("active");
  const category = watch("category");
  const unit = watch("unit");

  const clearForms = useCallback(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
  }, [setValue]);

  // Initialize form from item / defaults
  useEffect(() => {
    clearErrors();

    const src = item || {};
    Object.keys(defaultValues).forEach((formname) => {
      setValue(
        formname,
        src[formname] !== undefined ? src[formname] : defaultValues[formname]
      );
    });

    if (!item) {
      clearForms();
    }
  }, [item, clearErrors, setValue, clearForms]);

  const handleRegister = useCallback(
    async (formdata) => {
      const payload = {
        ...formdata,
        id: item?.id,
        reorder_point: formdata.reorder_point
          ? Number(formdata.reorder_point)
          : null,
      };

      try {
        const result = await (onSave ? onSave(payload) : true);
        if (result !== false) {
          UIkit.offcanvas("#modal-storage-item-add").hide();
          reset(defaultValues);
        }
      } catch (err) {
        UIkit.notification("Failed to save item.", "error");
      }
    },
    [item, onSave, reset]
  );

  const handleError = useCallback((err) => {
    console.log(err);
  }, []);

  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    UIkit.offcanvas("#modal-storage-item-add").hide();
  }, [onCancel]);

  return (
    <div
      id="modal-storage-item-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={handleClose}
          ></button>

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {flagIsNew ? "Add Item" : "Edit Item"}
            </h2>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <form
                onSubmit={handleSubmit(handleRegister, handleError)}
                data-uk-grid="true"
              >
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Item Name*</label>
                  <div className="uk-form-controls">
                    <input
                      className={classnames("uk-input", {
                        "uk-form-danger": errors.name,
                      })}
                      type="text"
                      {...register("name")}
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">SKU / Code</label>
                  <div className="uk-form-controls">
                    <input
                      className={classnames("uk-input", {
                        "uk-form-danger": errors.sku,
                      })}
                      type="text"
                      {...register("sku")}
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Category*</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <select {...register("category")}>
                        {ITEM_CATEGORY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className={classnames("uk-button uk-button-default", {
                          "uk-form-danger": errors.category,
                        })}
                        type="button"
                        tabIndex="-1"
                      >
                        <span className="select-value">
                          {ITEM_CATEGORY_OPTIONS.find(
                            (o) => o.value === category
                          )?.label || "Select a Category"}
                        </span>
                        <span
                          uk-icon="icon: chevron-down"
                          className="hms-align-right"
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Unit*</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <select {...register("unit")}>
                        {UNIT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className={classnames("uk-button uk-button-default", {
                          "uk-form-danger": errors.unit,
                        })}
                        type="button"
                        tabIndex="-1"
                      >
                        <span className="select-value">
                          {UNIT_OPTIONS.find((o) => o.value === unit)?.label ||
                            "Select a Unit"}
                        </span>
                        <span
                          uk-icon="icon: chevron-down"
                          className="hms-align-right"
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reorder point */}
                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Reorder Point (optional)</label>
                  <div className="uk-form-controls">
                    <input
                      className={classnames("uk-input", {
                        "uk-form-danger": errors.reorder_point,
                      })}
                      type="number"
                      min="0"
                      {...register("reorder_point")}
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Notes</label>
                  <div className="uk-form-controls">
                    <textarea
                      className="uk-textarea"
                      rows="2"
                      {...register("notes")}
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Status</label>
                  <div className="uk-form-controls">
                    <div className="autosend-checkbox">
                      <label className="setting-switch">
                        <input
                          type="checkbox"
                          checked={!!active}
                          onChange={(e) =>
                            setValue("active", e.target.checked)
                          }
                        />
                        <span className="setting-slider setting-round"></span>
                      </label>
                      <span className="uk-margin-small-left">
                        {active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="uk-width-1-1 uk-margin-top">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="submit"
                  >
                    {flagIsProcessing ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close uk-margin-small-left"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageItemAdd;